if (!customElements.get('moud-featured-video-bubbles')) {
  // In-app browsers (TikTok, etc.) often refuse inline playback: any play() opens the native fullscreen player.
  const MFVB_NO_INLINE_UA = /musical_ly|BytedanceWebview|TikTok|trill_|Snapchat|Pinterest/i;

  class MoudFeaturedVideoBubbles extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[data-mfvb-dialog]');
      this.stage = this.querySelector('[data-mfvb-stage]');
      this.closeButton = this.querySelector('[data-mfvb-close]');
      this.openers = [...this.querySelectorAll('[data-mfvb-open]')];
      this.previews = [...this.querySelectorAll('[data-mfvb-preview]')];
      this.autoplayPreviews = this.dataset.autoplayPreviews === 'true' && !MFVB_NO_INLINE_UA.test(navigator.userAgent);
      this.lazyDistance = Number.parseInt(this.dataset.lazyDistance, 10) || 0;
      this.previousDocumentOverflow = '';
      this.lastOpener = null;

      this.onOpen = this.onOpen.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);

      this.openers.forEach((opener) => opener.addEventListener('click', this.onOpen));
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', this.onDialogClose);

      this.setupPreviewObservers();
    }

    disconnectedCallback() {
      this.openers?.forEach((opener) => opener.removeEventListener('click', this.onOpen));
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.lazyObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.stopModal();
      document.documentElement.style.overflow = this.previousDocumentOverflow;
      this.initialized = false;
    }

    setupPreviewObservers() {
      if (!this.previews.length) return;

      if (!('IntersectionObserver' in window)) {
        this.previews.forEach((preview) => {
          this.hydratePreview(preview);
          if (this.autoplayPreviews) this.playPreview(preview);
        });
        return;
      }

      this.lazyObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.hydratePreview(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: `${this.lazyDistance}px 0px` }
      );

      this.playbackObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.dataset.mfvbVisible = entry.isIntersecting ? 'true' : 'false';
            if (!this.autoplayPreviews) return;
            if (entry.isIntersecting) {
              this.hydratePreview(entry.target);
              this.playPreview(entry.target);
            } else {
              this.pausePreview(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );

      this.previews.forEach((preview) => {
        this.lazyObserver.observe(preview);
        this.playbackObserver.observe(preview);
      });
    }

    hydratePreview(preview) {
      if (preview.dataset.mfvbHydrated === 'true') return;
      const template = preview.querySelector('[data-mfvb-preview-template]');
      if (!template) return;

      preview.append(template.content.cloneNode(true));
      preview.dataset.mfvbHydrated = 'true';
      preview.classList.add('is-loaded');

      const video = preview.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      // Safety net: if a webview still forces fullscreen on a preview, exit and stop autoplaying previews.
      video.addEventListener('webkitbeginfullscreen', () => {
        this.autoplayPreviews = false;
        video.pause();
        video.webkitExitFullscreen?.();
      });
      video.load();
    }

    playPreview(preview) {
      const video = preview.querySelector('video');
      if (!video || this.dialog?.open) return;
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    }

    pausePreview(preview) {
      preview.querySelector('video')?.pause();
    }

    pauseAllPreviews() {
      this.previews.forEach((preview) => this.pausePreview(preview));
    }

    resumeVisiblePreviews() {
      if (!this.autoplayPreviews) return;
      this.previews.forEach((preview) => {
        if (preview.dataset.mfvbVisible === 'true') this.playPreview(preview);
      });
    }

    onOpen(event) {
      if (!this.dialog || !this.stage) return;
      const opener = event.currentTarget;
      const template = this.querySelector(`[data-mfvb-modal-template="${opener.dataset.mfvbOpen}"]`);
      if (!template) return;

      this.lastOpener = opener;
      this.pauseAllPreviews();
      this.stage.replaceChildren(template.content.cloneNode(true));

      const iframe = this.stage.querySelector('[data-mfvb-src]');
      if (iframe) iframe.src = iframe.dataset.mfvbSrc;

      this.previousDocumentOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();

      const video = this.stage.querySelector('video');
      if (video) {
        video.load();
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      }
    }

    onCloseClick() {
      this.dialog?.close();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    onDialogClose() {
      this.stopModal();
      document.documentElement.style.overflow = this.previousDocumentOverflow;
      this.resumeVisiblePreviews();
      this.lastOpener?.focus({ preventScroll: true });
    }

    stopModal() {
      if (!this.stage) return;
      this.stage.querySelector('video')?.pause();
      const iframe = this.stage.querySelector('iframe');
      if (iframe) iframe.src = 'about:blank';
      this.stage.replaceChildren();
    }
  }

  customElements.define('moud-featured-video-bubbles', MoudFeaturedVideoBubbles);
}
